import React from 'react'
import { useParams } from 'react-router-dom'
import { Grid, GridItem } from '@which/seatbelt'

import type { PensionsVerticalQuery } from '../../generated/frontend'
import { usePensionsVerticalQuery } from '../../generated/frontend'
import type { PageParams } from '../../routes'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import { Breadcrumb } from '../../shared/components/Breadcrumb/Breadcrumb'
import { ErrorComponent } from '../../shared/components/Error/ErrorComponent'
import { PageError } from '../../shared/components/Error/PageError'
import { Loader } from '../../shared/components/Loader/Loader'
import { ResubscribeBannerWrapper } from '../../shared/components/ResubscribeBannerWrapper/ResubscribeBannerWrapper'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import type { PageInfo } from '../../shared/types/PageInfo'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { removeUrlQueryStringAndFragment } from '../../shared/utils/remove-url-query-string-and-fragment'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import { VerticalHeader } from './components/VerticalHeader/VerticalHeader'
import { VerticalMore } from './components/VerticalMore/VerticalMore'
import { VerticalNavigation } from './components/VerticalNavigation/VerticalNavigation'
import styles from './template-styles/VerticalPage.module.scss'

const PensionsPage: React.FC = () => {
  const { verticalSlug } = useParams<PageParams>()
  const { loading, error, data } = usePensionsVerticalQuery({
    variables: { verticalSlug },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })
  const { getBaseMetaTags } = useBaseMetaTags()

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.verticalPage) {
    return <PageError pageName="Pensions Vertical Page" />
  }

  const { meta, header, pensionScheme, showResubBanner } = data.verticalPage

  const baseMetaTags = getBaseMetaTags({ meta })
  const metaTags = getMetaTags({
    meta,
    baseMetaTags,
    verticalSlug,
    urls: { currentUrl: getFullUrl(), previousUrl },
  })

  return (
    <div data-testid="template-portal" data-template-type="pensions-vertical">
      <ResubscribeBannerWrapper showBanner={showResubBanner} />
      <Breadcrumb heading={{ text: 'Pensions' }} currentPage={{ text: '', href: '' }} links={[]} />
      <PageWrapperWithMeta metaTags={metaTags}>
        <>
          <Grid>
            <GridItem span={{ medium: 8, large: 8 }} columnStart={{ medium: 3, large: 3 }}>
              <VerticalHeader
                description={header.description}
                title={header.title}
                standfirst={header.standfirst}
                size={5}
                className={styles.verticalHeader}
              />
            </GridItem>
          </Grid>
          <Grid>
            <GridItem span={{ medium: 8, large: 8 }} columnStart={{ medium: 3, large: 3 }}>
              <VerticalNavigation data={pensionScheme} type="articles" />
            </GridItem>
          </Grid>
          {pensionScheme?.promoUrls?.length > 0 && (
            <section className={styles.awPromoBlockWrapper}>
              <div className="w-page-wrapper">
                <GridItem span={{ medium: 12, large: 12 }}>
                  <VerticalNavigation data={pensionScheme} type="widgets" />
                </GridItem>
              </div>
            </section>
          )}
          <VerticalMore data={pensionScheme} />
        </>
      </PageWrapperWithMeta>
    </div>
  )
}

export default PensionsPage

export type PensionsVerticalData = PensionsVerticalQuery['verticalPage'] & {
  __typename: 'VerticalPage'
}

const getMetaTags = ({
  meta,
  baseMetaTags,
  verticalSlug,
  urls,
}: {
  meta: PensionsVerticalData['meta']
  baseMetaTags: JSX.Element[]
  verticalSlug: string
  urls: { currentUrl: string; previousUrl?: string }
}) => {
  const pageInfo: PageInfo = {
    wcdPageUrl: removeUrlQueryStringAndFragment(urls.currentUrl),
    previousWcdPageUrl: urls.previousUrl || '',
    wcdPageTitle: meta?.title || '',
    vertical: verticalSlug,
    pageType: 'article-listing',
    content_type: 'pension-scheme',
  }

  // data layer
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(pageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, pageInfo)

  return baseMetaTags.concat(dataLayer, dfpMeta)
}
